import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { fetchExploitRuleHosts } from '../api/exploit-rules';
import { fetchProxyHosts } from '../api/proxy-hosts';
import type { ExploitRuleHostConfig } from '../types/exploit-rules';
import type { ProxyHost } from '../types/proxy-host';
import { HelpTip } from './common/HelpTip';
import {
  GlobalExploitRules,
  ExploitHostCard,
  HostExploitRulesModal,
  ExceptionsTab,
  HostExceptionsModal,
} from './exploit-rules';

export function ExploitBlockRules() {
  const { t } = useTranslation('exploitRules');
  const [mainTab, setMainTab] = useState<'global' | 'hosts' | 'exceptions'>('global');
  const [selectedHost, setSelectedHost] = useState<ExploitRuleHostConfig | null>(null);
  const [selectedExceptionHost, setSelectedExceptionHost] = useState<ProxyHost | null>(null);

  const hostsQuery = useQuery({
    queryKey: ['exploit-rule-hosts'],
    queryFn: fetchExploitRuleHosts,
  });

  const proxyHostsQuery = useQuery({
    queryKey: ['proxy-hosts'],
    queryFn: () => fetchProxyHosts(1, 100),
  });

  // Filter hosts that have block_exploits enabled (for exceptions tab)
  const exploitEnabledHosts = useMemo(() => {
    if (!proxyHostsQuery.data?.data) return [];
    return proxyHostsQuery.data.data.filter((host: ProxyHost) => host.block_exploits);
  }, [proxyHostsQuery.data]);

  const getSubtitle = () => {
    switch (mainTab) {
      case 'hosts':
        return t('hostList.subtitle');
      case 'exceptions':
        return t('exceptions.subtitle');
      default:
        return t('global.subtitle');
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">{t('title')}</h1>
          <p className="text-sm text-gray-500 dark:text-slate-400 mt-1">
            {getSubtitle()}
          </p>
        </div>
      </div>

      {/* Main Tabs */}
      <div className="border-b dark:border-slate-700">
        <div className="flex gap-4">
          <button
            onClick={() => setMainTab('global')}
            className={`py-3 px-1 border-b-2 font-medium text-sm transition-colors flex items-center gap-2 ${mainTab === 'global'
              ? 'border-amber-600 dark:border-amber-500 text-amber-600 dark:text-amber-500'
              : 'border-transparent text-gray-500 dark:text-slate-400 hover:text-gray-700 dark:hover:text-slate-200'
            }`}
          >
            {t('tabs.global')}
            <HelpTip contentKey="help.global.description" ns="exploitRules" />
          </button>
          <button
            onClick={() => setMainTab('hosts')}
            className={`py-3 px-1 border-b-2 font-medium text-sm transition-colors ${mainTab === 'hosts'
              ? 'border-amber-600 dark:border-amber-500 text-amber-600 dark:text-amber-500'
              : 'border-transparent text-gray-500 dark:text-slate-400 hover:text-gray-700 dark:hover:text-slate-200'
            }`}
          >
            {t('tabs.perHost')}
          </button>
          <button
            onClick={() => setMainTab('exceptions')}
            className={`py-3 px-1 border-b-2 font-medium text-sm transition-colors flex items-center gap-2 ${mainTab === 'exceptions'
              ? 'border-amber-600 dark:border-amber-500 text-amber-600 dark:text-amber-500'
              : 'border-transparent text-gray-500 dark:text-slate-400 hover:text-gray-700 dark:hover:text-slate-200'
            }`}
          >
            {t('tabs.exceptions')}
            <HelpTip contentKey="help.exceptions.description" ns="exploitRules" />
          </button>
        </div>
      </div>

      {mainTab === 'global' ? (
        <GlobalExploitRules />
      ) : mainTab === 'exceptions' ? (
        <ExceptionsTab
          exploitEnabledHosts={exploitEnabledHosts}
          isLoading={proxyHostsQuery.isLoading}
          isError={!!proxyHostsQuery.error}
          onManageHost={setSelectedExceptionHost}
        />
      ) : (
        <>
          {/* Host Cards */}
          {hostsQuery.isLoading ? (
            <div className="flex items-center justify-center h-32">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-600"></div>
            </div>
          ) : hostsQuery.error ? (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 text-red-600 dark:text-red-400">
              {t('hostList.loadError')}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {(hostsQuery.data?.hosts || []).map((host) => (
                <ExploitHostCard
                  key={host.proxy_host_id}
                  host={host}
                  onManage={() => setSelectedHost(host)}
                />
              ))}
              {(!hostsQuery.data?.hosts || hostsQuery.data.hosts.length === 0) && (
                <div className="col-span-full bg-gray-50 dark:bg-slate-800/50 rounded-lg p-8 text-center text-gray-500 dark:text-slate-400">
                  {t('hostList.noHosts')}
                </div>
              )}
            </div>
          )}
        </>
      )}

      {/* Host Rules Modal */}
      {selectedHost && (
        <HostExploitRulesModal
          host={selectedHost}
          onClose={() => setSelectedHost(null)}
        />
      )}

      {/* Host Exceptions Modal */}
      {selectedExceptionHost && (
        <HostExceptionsModal
          host={selectedExceptionHost}
          onClose={() => setSelectedExceptionHost(null)}
        />
      )}
    </div>
  );
}
